import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import {
  openTaskCaseDetailsInNewTab,
  type TaskCaseDetailsReturnState,
} from '../../pages/task-case/taskCaseDetailsReturnNavigation';
import {
  fetchConnectedAgdFileRefs,
  type ConnectedAgdFileRef,
} from '../../services/connectedAgdFileRefs';

type LinkedCasesModalProps = {
  open: boolean;
  caseRequestId: string | null;
  returnState?: TaskCaseDetailsReturnState;
  onClose: () => void;
};

export default function LinkedCasesModal({
  open,
  caseRequestId,
  returnState,
  onClose,
}: LinkedCasesModalProps) {
  const { t } = useTranslation();
  const { taskCasePath } = useTaskCaseDetailsNavigation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ConnectedAgdFileRef[]>([]);

  useEffect(() => {
    if (!open || !caseRequestId?.trim()) {
      setLoading(false);
      setError(null);
      setItems([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setItems([]);

    void fetchConnectedAgdFileRefs(caseRequestId, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res.ok) {
          setError(res.message);
          return;
        }
        setItems(res.items);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, caseRequestId]);

  return (
    <Modal
      open={open}
      title={t('tasks.shared.linkedCasesTitle')}
      onClose={onClose}
      boxClassName="linked-cases-modal"
      portal
    >
      {loading ? (
        <p className="linked-cases-empty">{t('common.loading')}</p>
      ) : error ? (
        <p className="linked-cases-empty">{error}</p>
      ) : items.length === 0 ? (
        <p className="linked-cases-empty">{t('tasks.shared.noLinkedCases')}</p>
      ) : (
        <ul className="linked-cases-list">
          {items.map((item, index) => {
            const linkedCaseRequestId = item.caseRequestId?.trim() ?? '';
            const path = linkedCaseRequestId
              ? taskCasePath({ caseRequestId: linkedCaseRequestId })
              : '';

            return (
              <li key={`${linkedCaseRequestId || 'ref'}-${item.connectedAgdFileRefNumber}-${index}`}>
                {linkedCaseRequestId ? (
                  returnState ? (
                    <button
                      type="button"
                      className="linked-cases-link"
                      onClick={() => openTaskCaseDetailsInNewTab(path, returnState)}
                    >
                      {item.connectedAgdFileRefNumber}
                    </button>
                  ) : (
                    <a href={path} target="_blank" rel="noopener noreferrer">
                      {item.connectedAgdFileRefNumber}
                    </a>
                  )
                ) : (
                  item.connectedAgdFileRefNumber
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="cn-footer cn-footer--plain">
        <button type="button" className="mfb mfb-dk" onClick={onClose}>
          {t('common.back')}
        </button>
      </div>
    </Modal>
  );
}
